import { useState, useEffect } from "react";
import { toast } from "sonner";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Panel, Loading } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, UserPlus, Send, Trash2, Copy } from "lucide-react";
import { fmtDateTime } from "@/lib/time";

const ROLES = [["driver", "Driver"], ["dispatcher", "Dispatcher"], ["supervisor", "Supervisor"], ["fleet_manager", "Fleet Manager"]];
const ROLE = Object.fromEntries(ROLES);
const field = "mt-1.5 border-slate-700 bg-slate-900 text-white placeholder:text-slate-500";
const EMPTY = { full_name: "", email: "", phone: "", role: "driver", license_number: "", license_expiration: "" };

const joinLink = (code) => `${window.location.origin}/join?code=${code}`;

export default function TeamPage() {
  const [invites, setInvites] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const load = () => fleetApi.get("/invites").then((r) => setInvites(r.data || [])).catch((e) => { setInvites([]); toast.error(fleetError(e, "Couldn't load invites.")); });
  useEffect(() => { load(); }, []);

  const emailInvite = async (id, quiet) => {
    try {
      await api.post("/team/invite-email", { invite_id: id });
      if (!quiet) toast.success("Invite email sent.");
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send the invite email.");
      return false;
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      const body = { ...f, email: f.email.trim().toLowerCase() };
      if (body.role !== "driver") { delete body.license_number; delete body.license_expiration; }
      const { data } = await fleetApi.post("/invites", body);
      const sent = data.emailed ? true : await emailInvite(data.id, true);
      if (sent) toast.success(`Invite sent to ${body.email} (code ${data.code}).`);
      else toast.message(`Invite created (code ${data.code}). Use Copy link to share it.`);
      setF(EMPTY);
      load();
    } catch (err) {
      toast.error(fleetError(err, "Couldn't create the invite."));
    } finally { setBusy(null); }
  };

  const resend = async (inv) => {
    setBusy(inv.id);
    try {
      const { data } = await fleetApi.post(`/invites/${inv.id}/resend`);
      const sent = data?.emailed ? true : await emailInvite(inv.id, true);
      if (sent) toast.success(`Invite re-sent to ${inv.email}.`);
    } catch (err) { toast.error(fleetError(err, "Couldn't resend.")); } finally { setBusy(null); }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Cancel the invite for ${inv.email}? The code will stop working.`)) return;
    setBusy(inv.id);
    try {
      await fleetApi.delete(`/invites/${inv.id}`);
      toast.success("Invite cancelled.");
      load();
    } catch (err) { toast.error(fleetError(err, "Couldn't cancel.")); } finally { setBusy(null); }
  };

  const pending = (invites || []).filter((i) => !i.consumed);
  return (
    <div data-testid="team-page">
      <PageHeader title="Team & Invites" subtitle="Invite drivers, dispatchers, supervisors and fleet managers. Their login works on the web portal and the RideStamp app." />
      <div className="grid gap-6 lg:grid-cols-5">
        <Panel title="Invite a team member" testid="team-invite-form" className="lg:col-span-2">
          <form onSubmit={invite} className="space-y-4 p-5">
            <div>
              <Label className="text-slate-300">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF((p) => ({ ...p, role: v }))}>
                <SelectTrigger className={field} data-testid="team-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`team-role-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {[["full_name", "Full name", "text", true], ["email", "Email", "email", true], ["phone", "Phone", "tel", false]].map(([k, l, t, req]) => (
              <div key={k}><Label className="text-slate-300">{l}</Label><Input type={t} value={f[k]} onChange={set(k)} required={req} data-testid={`team-${k}`} className={field} /></div>
            ))}
            {f.role === "driver" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300">License #</Label><Input value={f.license_number} onChange={set("license_number")} data-testid="team-license_number" className={field} /></div>
                <div><Label className="text-slate-300">License expires</Label><Input type="date" value={f.license_expiration} onChange={set("license_expiration")} data-testid="team-license_expiration" className={`${field} [color-scheme:dark]`} /></div>
              </div>
            )}
            <Button type="submit" disabled={busy === "create"} data-testid="team-invite-submit" className="w-full gap-2 bg-blue-700 text-white hover:bg-blue-800">
              {busy === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Send invite
            </Button>
            <p className="text-xs text-slate-500">They'll get an email with a join link and code. Drivers then sign in on the RideStamp app.</p>
          </form>
        </Panel>
        <Panel title={`Pending invites · ${pending.length}`} testid="team-invites" className="lg:col-span-3">
          {invites === null ? <Loading label="Loading invites…" /> : pending.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="team-invites-empty">No pending invites. Invite your first driver or dispatcher.</p>
          ) : (
            <ul className="divide-y divide-slate-800">
              {pending.map((inv) => (
                <li key={inv.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`team-invite-${inv.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-white">{inv.full_name || inv.email} <span className="ml-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[11px] font-semibold text-blue-300">{ROLE[inv.role] || inv.role}</span></div>
                    <div className="truncate text-xs text-slate-500">{inv.email} · code <span className="font-mono-data text-slate-300">{inv.code}</span> · {fmtDateTime(inv.created_at)}</div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(joinLink(inv.code)); toast.success("Join link copied."); }} className="gap-1 text-slate-300 hover:bg-slate-800 hover:text-white" data-testid={`team-copy-${inv.id}`}><Copy className="h-3.5 w-3.5" /> Link</Button>
                    <Button size="sm" variant="ghost" onClick={() => resend(inv)} disabled={busy === inv.id} className="gap-1 text-blue-300 hover:bg-slate-800" data-testid={`team-resend-${inv.id}`}><Send className="h-3.5 w-3.5" /> Resend</Button>
                    <Button size="sm" variant="ghost" onClick={() => revoke(inv)} disabled={busy === inv.id} className="gap-1 text-red-300 hover:bg-red-500/10" data-testid={`team-revoke-${inv.id}`}><Trash2 className="h-3.5 w-3.5" /> Cancel</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
